'use client'

import * as React from 'react'
import {
  Calendar as CalendarIcon,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
} from 'lucide-react'

import { cn } from '@repo/ui/lib/utils'
import { PopoverAnchor, PopoverContent } from '../Popover'
import { ScrollArea } from '../ScrollArea'
import { gridCellClass, gridKeyDownHandler, stepperIconClass } from './constants'

type StepperFrameProps = {
  stepBackLabel: string
  stepForwardLabel: string
  onStepBack: () => void
  onStepForward: () => void
  /** Disables the whole control; `stepBackDisabled`/`stepForwardDisabled` disable one chevron at a range bound. */
  disabled?: boolean
  stepBackDisabled?: boolean
  stepForwardDisabled?: boolean
  className?: string
  children: React.ReactNode
}

/** The always-visible control: back/forward chevrons around an editable value. Doubles as the popover anchor. */
export function StepperFrame(props: StepperFrameProps) {
  return (
    <PopoverAnchor asChild>
      <div
        className={cn(
          'bg-background inline-flex items-center gap-2 rounded-md border px-3 py-2 shadow-sm',
          // The whole-control disabled look: dim the frame, drop the affordance shadow, and show
          // a not-allowed cursor (the inner buttons stop receiving pointer events when disabled).
          props.disabled && 'cursor-not-allowed opacity-50 shadow-none',
          props.className,
        )}
      >
        <button
          type="button"
          onClick={props.onStepBack}
          aria-label={props.stepBackLabel}
          disabled={props.disabled || props.stepBackDisabled}
          className={stepperIconClass}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        {props.children}
        <button
          type="button"
          onClick={props.onStepForward}
          aria-label={props.stepForwardLabel}
          disabled={props.disabled || props.stepForwardDisabled}
          className={stepperIconClass}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </PopoverAnchor>
  )
}

type StepperValueButtonProps = {
  ariaLabel: string
  isOpen: boolean
  onToggle: () => void
  /** Hides the leading calendar icon shown before the value. */
  hideCalendarIcon?: boolean
  /** Fixed width of the whole value area (icon included), so the control doesn't resize as the value changes. */
  widthClass: string
  /** The popover content's element id, referenced by aria-controls while open. */
  popoverId: string
  /** The picker keeps this ref so closing the popover can return focus here. */
  buttonRef: React.RefObject<HTMLButtonElement | null>
  id?: string
  disabled?: boolean
  children: React.ReactNode
}

/** The value shown between the chevrons; clicking it toggles the picker popover. */
export function StepperValueButton(props: StepperValueButtonProps) {
  return (
    <button
      type="button"
      ref={props.buttonRef}
      id={props.id}
      onClick={props.onToggle}
      disabled={props.disabled}
      aria-label={props.ariaLabel}
      aria-haspopup="dialog"
      aria-expanded={props.isOpen}
      aria-controls={props.isOpen ? props.popoverId : undefined}
      className={cn('flex items-center justify-center gap-1 text-sm font-medium', props.widthClass)}
    >
      {!props.hideCalendarIcon && (
        <CalendarIcon className="text-muted-foreground h-4 w-4 shrink-0" />
      )}
      {/* aria-live announces the new value when the chevrons step it, since focus stays on the chevron. */}
      <span aria-live="polite" className="min-w-0 truncate whitespace-nowrap">
        {props.children}
      </span>
    </button>
  )
}

type StepperPopoverContentProps = {
  /** The picker's root element, so clicks on the anchor row don't dismiss the popover. */
  rootRef: React.RefObject<HTMLDivElement | null>
  /** Element id referenced by the value button's aria-controls. */
  id: string
  /** Where focus returns when the popover closes: the value button that opened it. */
  returnFocusRef: React.RefObject<HTMLButtonElement | null>
  className?: string
  children: React.ReactNode
}

/**
 * Popover body that stays open for clicks on the anchor row. Opening moves focus
 * to the roving tab stop (the selected cell) so the panel is keyboard-reachable;
 * closing returns focus to the value button.
 */
export function StepperPopoverContent(props: StepperPopoverContentProps) {
  const contentRef = React.useRef<HTMLDivElement>(null)
  return (
    <PopoverContent
      ref={contentRef}
      id={props.id}
      sideOffset={6}
      // The close animation runs at half speed so a just-clicked cell's selected state barely
      // shows before the panel is gone.
      className={cn(
        'w-[220px] overflow-hidden p-0 data-[state=closed]:duration-75',
        props.className,
      )}
      onOpenAutoFocus={(e) => {
        e.preventDefault()
        // Two separate queries: a combined selector would return the first match in
        // DOM order, and the header's chevrons precede the grid's tab stop.
        const content = contentRef.current
        const cell =
          content?.querySelector<HTMLElement>('[tabindex="0"]') ??
          content?.querySelector<HTMLElement>('button:not(:disabled)')
        cell?.focus()
      }}
      onCloseAutoFocus={(e) => {
        e.preventDefault()
        props.returnFocusRef.current?.focus()
      }}
      onInteractOutside={(e) => {
        if (props.rootRef.current?.contains(e.target as Node)) e.preventDefault()
      }}
    >
      {props.children}
    </PopoverContent>
  )
}

type PickerHeaderProps = {
  label: string
  chooseYearLabel: string
  isYearListOpen: boolean
  onToggleYearList: () => void
  stepBackLabel: string
  stepForwardLabel: string
  onStepBack: () => void
  onStepForward: () => void
  stepBackDisabled?: boolean
  stepForwardDisabled?: boolean
}

/** Popover header: back/forward chevrons around a button that toggles the year list. */
export function PickerHeader(props: PickerHeaderProps) {
  return (
    <div className="flex items-center justify-between border-b px-3 py-1.5">
      <button
        type="button"
        onClick={props.onStepBack}
        aria-label={props.stepBackLabel}
        disabled={props.stepBackDisabled}
        className={stepperIconClass}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={props.onToggleYearList}
        aria-label={props.chooseYearLabel}
        aria-expanded={props.isYearListOpen}
        className="hover:bg-accent hover:text-accent-foreground flex items-center gap-1 rounded px-1.5 py-0.5 text-sm font-medium transition-colors"
      >
        {props.label}
        {props.isYearListOpen ? (
          <ChevronUp className="text-muted-foreground h-4 w-4" />
        ) : (
          <ChevronDown className="text-muted-foreground h-4 w-4" />
        )}
      </button>
      <button
        type="button"
        onClick={props.onStepForward}
        aria-label={props.stepForwardLabel}
        disabled={props.stepForwardDisabled}
        className={stepperIconClass}
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  )
}

// The year list's vertical metrics, which the scroll window height is computed from.
// A row is a text-sm line (20px) plus the cells' py-1 (8px); rows are separated by
// gap-y-3 (12px) and the grid carries py-3 (24px total). These match the month grid,
// so at visibleRows={4} MonthPicker's two panels are the same height.
const YEAR_ROW_HEIGHT_PX = 28
const YEAR_ROW_GAP_PX = 12
const YEAR_LIST_PADDING_Y_PX = 24

type YearListSectionProps = {
  years: number[]
  /** The committed year, highlighted in the list; omit when nothing is selected yet. */
  selectedYear?: number
  /** The keyboard tab stop and initial scroll center; defaults to `selectedYear`. */
  tabStopYear?: number
  /** How many rows of the 3-column grid are visible before the list scrolls. */
  visibleRows?: number
  /** Explicit scroll-window height in px, to match a sibling panel exactly; overrides `visibleRows`. */
  height?: number
  onSelectYear: (year: number) => void
}

/** Scrollable year grid; centers the tab-stop year when it mounts. */
export function YearListSection({ visibleRows = 3, ...props }: YearListSectionProps) {
  const listRef = React.useRef<HTMLDivElement>(null)
  const tabStopYear = props.tabStopYear ?? props.selectedYear
  const initialYearRef = React.useRef(tabStopYear)

  React.useLayoutEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-year="${initialYearRef.current}"]`)
      ?.scrollIntoView({ block: 'center' })
  }, [])

  // A fixed height is what makes the Radix viewport scroll (a max-height leaves the root
  // content-sized, so the viewport never overflows); only apply it when the list is longer
  // than the visible window, so short lists don't leave empty space.
  const isOverflowing = props.years.length > visibleRows * 3
  const windowHeight =
    props.height ??
    visibleRows * YEAR_ROW_HEIGHT_PX + (visibleRows - 1) * YEAR_ROW_GAP_PX + YEAR_LIST_PADDING_Y_PX

  return (
    <ScrollArea
      type="auto"
      {...(isOverflowing && {
        style: { height: windowHeight },
      })}
    >
      <div
        ref={listRef}
        onKeyDown={gridKeyDownHandler(3)}
        className="grid grid-cols-3 gap-x-3 gap-y-3 px-3 py-3"
      >
        {props.years.map((year) => (
          <button
            key={year}
            type="button"
            data-year={year}
            tabIndex={year === tabStopYear ? 0 : -1}
            aria-pressed={year === props.selectedYear}
            onClick={() => props.onSelectYear(year)}
            className={cn('py-1', gridCellClass(year === props.selectedYear))}
          >
            {year}
          </button>
        ))}
      </div>
    </ScrollArea>
  )
}
